import { Box, Flex, Icon, Spinner, Text, VStack } from "@chakra-ui/react";
import { RiCheckboxCircleFill } from "react-icons/ri";
import { Glossary } from "src/components/Text/Glossary";
import type { ParentSummary } from "src/generated/hooks/form-service/requests";
import { useOrganization } from "src/hooks/useOrganization";
import { DuplicateParentCard } from "./DuplicateParentCard";
import { useParentDuplicateSearch } from "./useParentDuplicateSearch";

interface PossibleParentDuplicatesPanelProps {
  organizationId: string;
  // Select a matched parent in place instead of navigating to its edit page.
  onUseParent: (parent: ParentSummary) => void;
}

const CHECK_COPY =
  "Parent/guardians are checked against email and phone once either is entered.";

export function PossibleParentDuplicatesPanel({
  organizationId,
  onUseParent,
}: PossibleParentDuplicatesPanelProps) {
  const organization = useOrganization();
  const { isQualified, isPending, isError, hasSearched, matches, fullName } =
    useParentDuplicateSearch(organizationId);

  return (
    <Box
      border="1px solid"
      borderColor="gray.200"
      borderRadius="lg"
      backgroundColor="gray.50"
      padding={5}
      width="100%"
    >
      <Text
        textTransform="uppercase"
        letterSpacing="wider"
        fontSize="sm"
        fontWeight="700"
        color="gray.500"
        marginBottom={4}
      >
        <Glossary>Possible duplicates</Glossary>
      </Text>
      {renderBody()}
    </Box>
  );

  function renderBody() {
    if (!isQualified) {
      return (
        <Text fontSize="sm" color="gray.500">
          <Glossary>{CHECK_COPY}</Glossary>
        </Text>
      );
    }

    if (isError) {
      return (
        <Text fontSize="sm" color="red.600">
          Something went wrong checking for duplicates. Please try again.
        </Text>
      );
    }

    if (isPending || !hasSearched) {
      return (
        <Flex justifyContent="center" paddingY={4}>
          <Spinner />
        </Flex>
      );
    }

    if (matches.length > 0) {
      return (
        <Flex direction="column" gap={4}>
          <VStack align="stretch" spacing={3} maxHeight="60vh" overflowY="auto">
            {matches.map((parent) => (
              <DuplicateParentCard
                key={parent.id}
                parent={parent}
                organization={organization}
                onUse={onUseParent}
              />
            ))}
          </VStack>
          {renderCheckCopy()}
        </Flex>
      );
    }

    return (
      <Flex direction="column" gap={4}>
        <Flex align="flex-start" gap={2}>
          <Icon
            as={RiCheckboxCircleFill}
            color="green.500"
            boxSize={5}
            flexShrink={0}
          />
          <Text fontSize="sm" color="gray.700">
            <Text as="span" fontWeight="700">
              <Glossary>Parent/guardian</Glossary>:
            </Text>{" "}
            {`no record matches ${fullName || "this parent/guardian"}`}
          </Text>
        </Flex>
        {renderCheckCopy()}
      </Flex>
    );
  }

  function renderCheckCopy() {
    return (
      <Text fontSize="sm" color="gray.500">
        <Glossary>{CHECK_COPY}</Glossary>
      </Text>
    );
  }
}
